// Navigazione senza ricaricare la pagina: la scena 3D resta viva e la camera si sposta tra le sezioni.
// Ogni vista è caricata solo quando serve (import dinamico) e restituisce la funzione per smontarsi.

import type { Motore } from '../scena/motore';
import { impostaLingua, lingua, LINGUA_PREDEFINITA, risolvi, t, type Lingua, type Sezione } from '../i18n';
import { ricaricaSeVecchio } from './ricarica';

export interface Contesto {
  motore: Motore | null;
  url: URL;
}

export interface Vista {
  modalita: 'esplora' | 'sfondo';
  monta(contenitore: HTMLElement, ctx: Contesto): (() => void) | void;
}

const VISTE: Record<Sezione, () => Promise<{ default: Vista }>> = {
  esplora: () => import('../viste/esplora'),
  lanci: () => import('../viste/lanci'),
  classifica: () => import('../viste/classifica'),
  potenze: () => import('../viste/potenze'),
  storia: () => import('../viste/storia'),
  eventi: () => import('../viste/eventi'),
  metodologia: () => import('../viste/metodologia'),
  fonti: () => import('../viste/fonti'),
  dati: () => import('../viste/dati'),
  stato: () => import('../viste/stato'),
  privacy: () => import('../viste/privacy'),
};

export interface Cambio { sezione: Sezione; lingua: Lingua; linguaCambiata: boolean }

export class Router {
  private smonta: (() => void) | null = null;
  private corrente = '';
  onCambio: ((c: Cambio) => void) | null = null;

  constructor(private readonly contenitore: HTMLElement, private readonly motore: Motore | null) {
    document.addEventListener('click', (e) => {
      const a = (e.target as Element).closest?.('a');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || a.target === '_blank' || a.hasAttribute('download')) return;
      if (!risolvi(url.pathname)) return;
      e.preventDefault();
      this.vai(url.pathname + url.search + url.hash);
    });
    window.addEventListener('popstate', () => this.mostra(new URL(location.href)));
  }

  vai(indirizzo: string, sostituisci = false): void {
    if (indirizzo === location.pathname + location.search + location.hash) return;
    if (sostituisci) history.replaceState(null, '', indirizzo);
    else history.pushState(null, '', indirizzo);
    this.mostra(new URL(location.href));
  }

  /** Lingua e dock come per `mostra`, ma la vista non si monta: resta la pagina arrivata dal Worker (titolo,
   *  descrizione e scheda leggibile). Per i motori di ricerca sulle pagine di dettaglio: montando la sezione,
   *  Google vedeva l'elenco dei lanci (o la classifica, o la home) al posto del razzo e sceglieva la sezione come
   *  canonica («pagina duplicata», 365 pagine al 23 ott 2026). Al primo clic su un link si torna alla navigazione normale. */
  fissa(url: URL): void {
    const r = risolvi(url.pathname) ?? { lingua: LINGUA_PREDEFINITA, sezione: 'esplora' as Sezione };
    this.corrente = `${r.lingua}:${r.sezione}`;
    const linguaCambiata = r.lingua !== lingua();
    impostaLingua(r.lingua);
    document.documentElement.dataset.modalita = 'sfondo';
    document.documentElement.dataset.sezione = r.sezione;
    this.motore?.impostaModalita('sfondo');
    this.onCambio?.({ sezione: r.sezione, lingua: r.lingua, linguaCambiata });
  }

  /** Aggiorna solo la query (es. ?oggetto=…) senza rimontare la vista. */
  aggiornaQuery(parametri: Record<string, string | null>): void {
    const url = new URL(location.href);
    for (const [k, v] of Object.entries(parametri)) {
      if (v === null) url.searchParams.delete(k); else url.searchParams.set(k, v);
    }
    history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  async mostra(url: URL): Promise<void> {
    const r = risolvi(url.pathname) ?? { lingua: LINGUA_PREDEFINITA, sezione: 'esplora' as Sezione };
    const chiave = `${r.lingua}:${r.sezione}`;
    if (chiave === this.corrente) return;
    this.corrente = chiave;
    const linguaCambiata = r.lingua !== lingua();
    impostaLingua(r.lingua);
    document.title = t().rotte[r.sezione].titolo;
    document.querySelector('meta[name="description"]')?.setAttribute('content', t().rotte[r.sezione].descrizione);
    this.onCambio?.({ sezione: r.sezione, lingua: r.lingua, linguaCambiata });

    let modulo: { default: Vista } | undefined;
    // Tempo massimo: su telefono, con una pagina vecchia o la rete incerta, l'import poteva restare appeso senza errori
    // e la sezione non si apriva mai («devo ricaricare»). Dopo 12 s si considera fallito.
    const scaduto = new Promise<undefined>((ok) => setTimeout(() => ok(undefined), 12_000));
    try { modulo = await Promise.race([VISTE[r.sezione](), scaduto]); } catch { modulo = undefined; }
    if (!modulo?.default) {
      // Il sito è stato ripubblicato mentre la pagina era aperta: i file hanno nomi nuovi e quello richiesto non esiste
      // più. Senza questo la sezione restava «muta» a ogni clic finché non si ricaricava a mano.
      this.corrente = '';
      if (ricaricaSeVecchio(url)) return;
      console.error(`ArkOrb: sezione «${r.sezione}» non caricabile`);
      return;
    }
    if (this.corrente !== chiave) return; // nel frattempo si è navigato altrove
    const vista = modulo.default;

    this.smonta?.();
    this.smonta = null;
    this.contenitore.replaceChildren();
    document.documentElement.dataset.modalita = vista.modalita;
    document.documentElement.dataset.sezione = r.sezione;
    this.motore?.impostaModalita(vista.modalita);
    this.contenitore.scrollTop = 0;
    this.smonta = vista.monta(this.contenitore, { motore: this.motore, url }) ?? null;
    // Tastiera e lettori di schermo ripartono dal nuovo contenuto, non dal link del dock appena premuto.
    if (this.giaMostrata) this.contenitore.focus({ preventScroll: true });
    this.giaMostrata = true;
  }

  private giaMostrata = false;
}

export let router: Router;
export function creaRouter(contenitore: HTMLElement, motore: Motore | null): Router {
  router = new Router(contenitore, motore);
  return router;
}
